<template>
    <app-layout title="Pick Order">

        <page-header>
            <template #title>Pick Order</template>

            <template #actions>
                <jet-tertiary-button type="button" class="leading-none" @click="backUrl">
                    Close
                </jet-tertiary-button>
            </template>
        </page-header>



        <div
            v-if="mode === 'notes'"
            class="px-3 md:px-4 lg:px-8 gap-y-4 max-w-2xl"
        >
            <div
                v-for="(order, ackIndex) in ordersRequiringNotesAcknowledgement"
                :key="'ack-' + order.id"
                :class="{ 'border-t border-gray-300 pt-6 mt-6': ackIndex > 0 }"
            >
                <picking-red-flag-alert :customer="order.customer" />
                <div class="mb-4">
                    <div class="text-sm text-gray-500">Order #</div>
                    <div class="text-xl font-bold">{{ order.id }}</div>
                </div>
                <div class="mb-4">
                    <div class="text-sm text-gray-500">Customer</div>
                    <div class="text-xl font-bold">{{ notesModeCustomerName(order) }}</div>
                </div>
                <div v-if="order.picking_notes && order.picking_notes.trim()" class="mb-4">
                    <div class="text-sm font-semibold text-gray-700 mb-1">Order Picking Notes</div>
                    <div class="p-3 bg-gray-100 rounded whitespace-pre-wrap">{{ order.picking_notes }}</div>
                </div>
                <div v-if="order.customer?.picking_notes && order.customer.picking_notes.trim()" class="mb-4">
                    <div class="text-sm font-semibold text-gray-700 mb-1">Customer Notes</div>
                    <div class="p-3 bg-gray-100 rounded whitespace-pre-wrap">{{ order.customer.picking_notes }}</div>
                </div>
            </div>
            <div class="mt-6">
                <jet-button
                    class="leading-none"
                    :disabled="!notesCountdownReady"
                    @click="acknowledgeNotes"
                >
                    Acknowledge
                </jet-button>
                <div v-if="!notesCountdownReady" class="text-lg mt-2">{{ notesCountdown }}</div>
            </div>
        </div>

        <div
            v-else
            class="px-3 md:px-4 lg:px-8 gap-y-4"
        >
            <div class="flex items-end gap-x-20 mb-8">
                <div>
                    <div class="font-bold">
                        Zones:
                    </div>
                    <div class="flex flex-wrap gap-x-1 gap-y-2">
                        <!-- Show "Initial Label" button for wholesale orders -->
                        <div v-if="isWholesale">
                            <jet-secondary-button
                                v-if="selectedZone === 'initial_label'"
                                class="leading-none mr-3"
                            >
                                Initial Label
                            </jet-secondary-button>
                            <jet-tertiary-button
                                v-else
                                class="leading-none mr-3"
                                @click="selectedZone = 'initial_label'"
                            >
                                Initial Label
                            </jet-tertiary-button>
                        </div>
                        
                        <!-- Show "Items" button for wholesale orders with pallet orders -->
                        <div v-if="isWholesale && hasPalletOrders">
                            <jet-secondary-button
                                v-if="selectedZone === 'items'"
                                class="leading-none mr-3"
                            >
                                Items
                            </jet-secondary-button>
                            <jet-tertiary-button
                                v-else
                                class="leading-none mr-3"
                                @click="selectedZone = 'items'"
                            >
                                Items
                            </jet-tertiary-button>
                        </div>
                        
                        <!-- Show zone buttons for non-pallet orders or retail orders -->
                        <template v-if="!isWholesale || !hasPalletOrders">
                            <div
                                v-for="zone in zones"
                                :key="zone"
                            >
                                <jet-secondary-button
                                    v-if="selectedZone == zone.toString()"
                                    class="leading-none mr-3"
                                >
                                    {{ zone == '0' ? 'No Zone' : zone }}
                                </jet-secondary-button>
                                <jet-button 
                                    v-else-if="isZoneFullyPicked(zone)"
                                    class="leading-none mr-3"
                                    @click="selectedZone = zone.toString()"
                                >
                                    {{ zone == '0' ? 'No Zone' : zone }}
                                </jet-button>

                                <jet-tertiary-button
                                    v-else
                                    class="leading-none mr-3"
                                    @click="selectedZone = zone.toString()"
                                >
                                    {{ zone == '0' ? 'No Zone' : zone }}
                                </jet-tertiary-button>
                            </div>
                        </template>
                        
                        <div>
                            
                            <jet-secondary-button
                                v-if="selectedZone === 'summary'"
                                class="leading-none mr-3"
                            >
                                Summary
                            </jet-secondary-button>

                            <jet-tertiary-button
                                v-else
                                class="leading-none mr-3"
                                @click="selectedZone = 'summary'"
                                :disabled="!allItemPickedAndHasQuantity"
                            >
                                Summary
                            </jet-tertiary-button>
                        </div>
                    </div>
                </div>
            </div>

            <div
                v-for="(order, index) in orders"
                :key="order.id"
                class="flex flex-col -m-4"
            >
                <!-- Divider -->
                <div v-if="index != 0" class="border-t-8 border-b-8 border-dashed border-red-500 h-0 w-full"/>


                <div
                    class="relative overflow-hidden rounded-lg"
                    :class="[
                        index % 2 === 1 ? 'bg-blue-100' : '',
                        pickingRedFlagActive(order)
                            ? ['ring-4', 'ring-inset', 'ring-red-500', 'px-4', 'pb-10', index === 0 ? 'pt-14' : 'pt-20']
                            : ['p-4', index !== 0 ? 'pt-8' : ''],
                    ]"
                >
                    <div v-if="pickingRedFlagActive(order)" class="absolute top-4 right-4 z-10 text-red-600" style="font-size: 2.5rem;" aria-hidden="true">
                        <i class="fa-solid fa-flag"></i>
                    </div>
                    <picking-red-flag-alert
                        v-if="order.is_wholesale && order.customer?.picking_red_flag"
                        :customer="order.customer"
                        compact
                    />
                    <div
                        class="flex items-center gap-4 mb-4"
                    >
                        <!-- Layer Indicator -->
                        <div v-if="orderIds.length > 1" class="flex flex-col gap-1">
                            <div
                                v-for="(orderId, orderIdIndex) in orderIdsForIndicator"
                                :key="orderId"
                                class="h-12 w-36 rounded-md text-2xl font-bold text-white text-center flex justify-center items-center"
                                :class="{
                                    'bg-blue-500': orderIds.length === 1 || orderIdIndex == index,
                                    'bg-gray-300': orderIds.length !== 1 && orderIdIndex !== index
                                }"
                            >
                                <span v-if="orderIds.length === 1 || orderIdIndex == index">
                                    <span v-if="orderIdIndex === 0">
                                        <i class="text-lg" :class="getLayerIconClass(orderIdIndex)"></i> Top <i class="text-lg" :class="getLayerIconClass(orderIdIndex)"></i> 
                                    </span>
                                    <span v-else-if="orderIdIndex === (orderIdsForIndicator.length - 1)">
                                        <i class="text-lg" :class="getLayerIconClass(orderIdIndex)"></i> Bottom <i class="text-lg" :class="getLayerIconClass(orderIdIndex)"></i> 
                                    </span>
                                    <span v-else>
                                        <i class="text-lg" :class="getLayerIconClass(orderIdIndex)"></i> Middle <i class="text-lg" :class="getLayerIconClass(orderIdIndex)"></i> 
                                    </span>
                                </span>
                            </div>
                        </div>

                        <div>
                            <!-- Order Number -->
                            <div class="text-6xl font-bold">
                                {{ order.order_number }}
                                <span v-if="order.is_wholesale && order.customer?.name"> - {{ order.customer.name }}</span>
                                <span v-else-if="!order.is_wholesale && (order.shipping_first_name || order.shipping_last_name)"> - {{ order.shipping_first_name }} {{ order.shipping_last_name }}</span>

                                <!-- Picked Indicator -->
                                <span
                                    v-if="order.picked_at !== null"
                                    class="text-green"
                                >
                                    <i class="fa-solid fa-circle-check"></i>
                                </span>
                            </div>
                            
                            <div class="flex items-center gap-x-4">
                                <!-- Shipping Method -->
                                <div v-if="order.shipping_method === 'pickup'" class="px-2 py-1 rounded-md text-xl font-bold text-white text-center flex justify-center items-center gap-x-2 bg-red-500">
                                    <i class="fa-solid fa-people-carry-box"></i> Pickup
                                </div>
                                <div v-else-if="order.shipping_method === 'delivery'" class="px-2 py-1 rounded-md text-xl font-bold text-white text-center flex justify-center items-center gap-x-2 bg-yellow-500">
                                    <i class="fa-solid fa-truck"></i> Delivery
                                </div>
                                
                                <!-- Shipping Method -->
                                <div v-if="order.is_wholesale && order.customer">
                                    <div v-if="order.customer.pallet_orders" class="px-2 py-1 rounded-md text-xl font-bold text-white text-center flex justify-center items-center gap-x-2 bg-blue-500">
                                        <i class="fa-solid fa-pallet"></i> Pallet
                                    </div>
                                    <div v-else class="px-2 py-1 rounded-md text-xl font-bold text-white text-center flex justify-center items-center gap-x-2 bg-blue-500">
                                        <i class="fa-solid fa-cart-shopping"></i> Cart
                                    </div>
                                </div>

                                <!-- Substitutions Allowed -->
                                <div v-if="order.allow_substitutions" class="px-2 py-1 rounded-md text-xl font-bold text-white text-center flex justify-center items-center gap-x-2 bg-green">
                                    <i class="fa-solid fa-circle-check"></i> Substitutions Allowed
                                </div>
                                <div v-else class="px-2 py-1 rounded-md text-xl font-bold text-white text-center flex justify-center items-center gap-x-2 bg-red-500">
                                    <i class="fa-solid fa-circle-xmark"></i> Substitutions Not Allowed
                                </div>

                                <!-- Total Items -->
                                <div class="px-2 py-1 rounded-md text-xl font-bold text-white text-center flex justify-center items-center gap-x-2 bg-gray-500">
                                    <i class="fa-solid fa-basket-shopping"></i> {{ flatOrderItems ? flatOrderItems.filter(item => item.order_id === order.id).reduce((acc, item) => acc + (item.orig_quantity ?? 0), 0) : 0 }} Total Items Ordered
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Initial Label Zone -->
                    <div v-if="selectedZone === 'initial_label'" style="min-height: 15vh;">
                        <div class="text-lg text-gray-600 mb-2">
                            This will print a "0 of 0" label to identify the order on the {{ order.customer.pallet_orders ? 'pallet' : 'cart' }}.
                        </div>
                        
                        <jet-secondary-button type="button" class="leading-none" @click="printInitialLabels(order)">
                            <img src="/images/icons/print_w.svg" width="16" height="16" style="min-width: 16px;" alt="Print Initial Label">
                            <span class="ml-1">Print Initial Label</span>
                        </jet-secondary-button>
                    </div>

                    <!-- Summary -->
                    <div v-if="selectedZone === 'summary'" style="min-height: 15vh;">
                        <div class="flex items-end gap-x-4">
                            <!-- Box Count -->
                            <div>
                                <jet-label for="box_count" class="mb-0">Boxes</jet-label>
                                <InputGroup>
                                    <InputGroupText position="prepend"> 
                                        <BetterTooltip>
                                            <template #reference>
                                                <font-awesome-icon :icon="['fas', 'box']" />
                                            </template>
                                            <template #floating>
                                                <span class="text-xs">Boxes</span>
                                            </template>
                                        </BetterTooltip>
                                    </InputGroupText>
                                    <jet-input type="number" class="w-12 rounded-l-none text-center" name="box_count" v-model="order.box_count" @keydown.enter.prevent @input="autoSaveOrderPackaging(order.id)"/>
                                </InputGroup>
                            </div>

                            <!-- Pallet Count -->
                            <div v-if="isWholesale">
                                <jet-label for="pallet_count" class="mb-0">Pallets</jet-label>
                                <InputGroup>
                                    <InputGroupText position="prepend">
                                        <BetterTooltip>
                                            <template #reference>
                                                <font-awesome-icon :icon="['fas', 'pallet']" />
                                            </template>
                                            <template #floating>
                                                <span class="text-xs">Pallets</span>
                                            </template>
                                        </BetterTooltip>
                                    </InputGroupText>
                                    <jet-input type="number" class="w-12 rounded-l-none text-center" name="pallet_count" v-model="order.pallet_count" @keydown.enter.prevent @input="autoSaveOrderPackaging(order.id)"/>
                                </InputGroup>
                            </div>

                            <!-- Tote Count -->
                            <div v-else-if="order.shipping_method == 'delivery'">
                                <jet-label for="tote_count" class="mb-0">Totes</jet-label>
                                <InputGroup>
                                    <InputGroupText position="prepend">
                                        <BetterTooltip>
                                            <template #reference>
                                                <font-awesome-icon :icon="['fas', 'snowflake']" />
                                            </template>
                                            <template #floating>
                                                <span class="text-xs">Totes</span>
                                            </template>
                                        </BetterTooltip>
                                    </InputGroupText>
                                    <jet-input type="number" class="w-12 rounded-l-none text-center" name="tote_count" v-model="order.tote_count" @keydown.enter.prevent @input="autoSaveOrderToteCount(order.id)"/>
                                </InputGroup>
                            </div>

                            <jet-secondary-button :disabled="order.box_count <= 0" type="button" class="leading-none" @click="printLabels(order)">
                                <img src="/images/icons/print_w.svg" width="16" height="16" style="min-width: 16px;" alt="Print Labels">
                            </jet-secondary-button>

                            <jet-button :disabled="(!order.is_wholesale && order.box_count <= 0) || order.picked_at !== null" class="leading-none ml-8 h-8" @click="markOrderPicked(order.id)">
                                Complete Picking
                            </jet-button>
                        </div>
                    </div>

                    <!-- Items -->
                    <div v-else-if="selectedZone !== 'summary' && selectedZone !== 'initial_label'">
                        <!-- No Items -->
                        <div v-if="getItemsForOrder(order.id).length === 0" style="min-height: 15vh;">
                            This order has no items to display!
                        </div>

                        <div v-else class="grid grid-cols-2 gap-4">
                            <!-- Item -->
                            <div
                                v-for="item in getItemsForOrder(order.id)"
                                :key="item.id"
                                style="height: 40vh;"
                            >
                                <!-- Title -->
                                <div>
                                    <!-- Wholesale Title -->
                                    <template v-if="isWholesale">
                                        <div v-if="item.parent_id" class="text-2xl italic text-red-600">
                                            Part of "{{ item.parent_item.title }}
                                            <span v-if="item.parent_item.description"> - {{ item.parent_item.description }}</span>
                                            <span v-if="item.parent_item.size"> - {{ item.parent_item.size }}</span>
                                            <span v-else-if="item.parent_item.product?.size"> - {{ item.parent_item.product.size }}</span>
                                            <span v-else-if="item.parent_item.product?.unit_of_measure?.name"> - {{ item.parent_item.product.unit_of_measure.name }}</span>
                                            <span v-if="item.parent_item.product_id != null"> - Item #{{ item.parent_item.product_id }}</span>"
                                        </div>

                                        <div
                                            class="text-2xl font-bold"
                                            :class="{ 'text-red-600': item.product?.clearance }"
                                        >
                                            {{ item.title }}
                                            <span v-if="item.description"> - {{ item.description }}</span>
                                            <span v-if="item.size"> - {{ item.size }}</span>
                                            <span v-else-if="item.product?.size"> - {{ item.product.size }}</span>
                                            <span v-else-if="item.product?.unit_of_measure?.name"> - {{ item.product.unit_of_measure.name }}</span>
                                            <span v-if="item.product_id != null"> - Item #{{ item.product_id }}</span>
                                            <span v-if="item.product?.special_order" class="ml-2 italic font-extrabold leading-tight text-primary-red-500"> Special Order Item</span>
                                        </div>

                                        <Pill v-if="item.product_notes" color="red" size="sm">
                                            {{ item.product_notes }}
                                        </Pill>
                                    </template>
                                    <!-- Retail Title -->
                                    <template v-else>
                                        <div v-if="item.parent_id" class="text-2xl italic text-red-600">
                                            Part of "{{ item.parent_item.title }}
                                            <span v-if="item.parent_item.description"> - {{ item.parent_item.description }}</span>
                                            <span v-else-if="item.parent_item.product?.size"> - {{ item.parent_item.product.size }}</span>
                                            <span v-else-if="item.parent_item.product?.unit_of_measure?.name"> - {{ item.parent_item.product.unit_of_measure.name }}</span>
                                            <span v-if="item.parent_item.description_alt"> - {{ item.parent_item.description_alt }}</span>
                                            <span v-if="item.parent_item.product_id != null"> - Item #{{ item.parent_item.product_id }}</span>"
                                        </div>
                                        
                                        <div
                                            class="text-2xl font-bold"
                                            :class="{ 'text-red-600': item.product?.clearance }"
                                        >
                                            {{ item.title }}
                                            <span v-if="item.description"> - {{ item.description }}</span>
                                            <span v-else-if="item.product?.size"> - {{ item.product.size }}</span>
                                            <span v-else-if="item.product?.unit_of_measure?.name"> - {{ item.product.unit_of_measure.name }}</span>
                                            <span v-if="item.product_id != null"> - Item #{{ item.product_id }}</span>
                                            <span v-if="item.product?.special_order" class="ml-2 italic font-extrabold leading-tight text-primary-red-500"> Special Order Item</span>
                                        </div>

                                        <div v-if="item.description_alt" class="text-lg font-medium">
                                            {{ item.description_alt }}
                                        </div>
                                </template>
                            </div>

                            <div class="flex gap-x-2 sm:gap-x-6 lg:gap-x-12 min-w-0">
                                <div class="flex-shrink min-w-0">
                                    <!-- Product Details -->
                                    <div>
                                        <!-- Wholesale -->
                                        <template v-if="isWholesale">
                                            <div v-if="item.picking_zone" class="text-lg font-bold">
                                                Zone {{ item.picking_zone }}<span v-if="item.picking_subzone">.{{ item.picking_subzone }}</span>
                                            </div>
                                        </template>
                                        <!-- Retail -->
                                        <template v-else>
                                            <div v-if="item.picking_zone" class="text-lg font-bold">
                                                Zone {{ item.picking_zone }}<span v-if="item.picking_subzone">.{{ item.picking_subzone }}</span>
                                            </div>
                                        </template>
                                    </div>

                                    <div class="text-2xl font-bold mt-4"
                                        :class="{
                                            'text-green-500': item.picking_status === 'picked',
                                            'text-yellow-500': item.picking_status === 'shorted'
                                        }"
                                    >
                                        {{ item.picked_quantity ?? 0 }} of {{ item.orig_quantity }}<span v-if="itemUnitOfMeasure(item)">&nbsp;({{ itemUnitOfMeasure(item) }})</span>
                                    </div>
                                    <div v-if="item.parent_id && item.parent_item.orig_quantity > 0" class="text-lg font-bold text-red-600 mt-1">
                                        {{ formatMinDecimals(item.orig_quantity / item.parent_item.orig_quantity) }} per box
                                    </div>

                                    <div class="mt-6">
                                        <jet-button
                                            v-if="!pickOneByOne || (item.picked_quantity ?? 0) + 1 >= (item.orig_quantity ?? 0)" 
                                            class="text-2xl px-6 py-3"
                                            :color="index % 2 == 1 ? 'blue' : 'green'"
                                            @click="updateOrderItem(item, 'picked')" 
                                            :disabled="updatingOrderItem || item.picking_status !== null"
                                        >
                                            Picked
                                        </jet-button>
                                        <jet-button
                                            v-else
                                            class="text-2xl px-6 py-3"
                                            :color="index % 2 == 1 ? 'blue' : 'green'"
                                            @click="updatePickedQuantityByOne(item.id)"
                                            :disabled="item.picking_status !== null"
                                        >
                                            Picked
                                        </jet-button>
                                    </div>

                                    <div class="mt-6">
                                        <jet-tertiary-button
                                            class="text-2xl px-6 py-3"
                                            @click="handleShort(item)"
                                            :disabled="item.picking_status !== null"
                                        >
                                            Short
                                        </jet-tertiary-button>
                                    </div>

                                    <div v-if="updatingOrderItem && selectedItem.id === item.id" class="flex gap-x-1 text-sm mt-2 mx-6 bg-white border border-transparent rounded-md px-1">
                                        <img src="/images/icons/loading-pulse.svg" height="24" width="24" class="" />
                                        <div class="pt-1">
                                            Saving...
                                        </div>
                                    </div>

                                    <div v-if="errorMessage" class="text-red-500 mx-6 text-xl">
                                        {{ errorMessage }}
                                    </div>

                                </div>

                                <div class="flex-grow min-w-0">
                                    <img
                                        v-show="item.product.image"
                                        :src="'/storage/' + item.product.image"
                                        :key="item.product.image"
                                        alt="Product Preview"
                                        style="width: auto; height: calc(40vh - 5.25rem); object-fit: contain;"
                                        class="border border-transparent rounded-2xl"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            </div>
        </div>

        <!-- Picker Login -->
        <jet-dialog-modal :show="showPickerCodeModal" @close="cancelPickerCode">
            <template #title>
                    <h3 class="text-2xl">Update Picker</h3>
            </template>

            <template #content>
                <form @submit.prevent="updatePicker">
                    <div class="mb-2">
                        <jet-label value="Code:" class="mb-0" />
                        <jet-input v-model="pickerCode" ref="pickerCode" type="text" class="w-full" required />
                        <jet-input-error :message="pickerCodeError" class="m1" />
                    </div>

                    <input type="submit" class="hidden" ref="submitPickerCode" />
                </form>
            </template>

            <template #footer>
                <jet-button class="mr-2" @click="$refs.submitPickerCode.click()">
                    Update
                </jet-button>

                <jet-tertiary-button @click="cancelPickerCode">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>
        
        
        <jet-dialog-modal :show="showQuantityModal" @close="closeQuantityModal">
            <template #title>
                    <h3 class="text-2xl">Picked Quantity</h3>
            </template>

            <template #content>
                <form @submit.prevent="shortOrderItem">
                    <div>
                        <jet-label value="Picked Quantity:" class="mb-0" />
                        <jet-input v-model.number="quantity" type="number" class="w-full" required :step="pickedQuantityStep" min="0" :max="selectedItem.quantity" />
                    </div>

                    <input type="submit" class="hidden" ref="submitNewQuantity" />
                </form>
            </template>

            <template #footer>
                <jet-button class="mr-2" @click="$refs.submitNewQuantity.click()">
                    Save
                </jet-button>

                <jet-tertiary-button @click="closeQuantityModal">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>

        <!-- Layer Placement Notification -->
        <div v-if="showLayerOverlay" class="fixed top-8 left-1/2 -translate-x-1/2 w-3/4 max-w-xl">
            <div :key="layerOverlayKey" class="relative bg-blue-500 text-white rounded-md p-4 text-center z-50 shadow-lg overflow-hidden animate-fade-in">
                <div class="text-3xl font-bold" v-html="layerOverlayMessage"></div>
                <div class="absolute bottom-0 left-0 h-2 bg-blue-600 transition-all duration-100 ease-linear" 
                     :style="{ width: overlayProgress + '%' }">
                </div>
            </div>  
        </div>

    </app-layout>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import { DateTime } from "luxon";
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetInput from '@/Jetstream/Input';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetDropdown from '@/Jetstream/Dropdown';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import OrderDetails from '@/Shared/Orders/OrderDetails';
import JetLabel from '@/Jetstream/Label';
import JetInputError from '@/Jetstream/InputError';
import InputGroup from '@/Jetstream/InputGroup';
import InputGroupText from '@/Jetstream/InputGroupText';
import BetterTooltip from '@/Jetstream/BetterTooltip';
import Pill from '@/Jetstream/Pill';
import PickingRedFlagAlert from '@/Components/PickingRedFlagAlert.vue';
import Multiselect from '@vueform/multiselect';

import '@vueform/multiselect/themes/default.css';

import axios from 'axios';
import mixins from '@/mixins';
import { orderItemQuantityStep } from '@/Composables/UseProductQuantity';

export default {
    props: ['order_items', 'origPicker', 'orders', 'requires_notes_acknowledgement'],

    mixins: [mixins],

    setup(props) {
        const { isAdmin } = UseUserAuth(usePage().props.value.user);

        return { isAdmin }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetInput,
        JetConfirmationModal,
        JetSecondaryButton,
        JetDangerButton,
        JetDialogModal,
        JetDropdown,
        JetTertiaryButton,
        OrderDetails,
        JetLabel,
        JetInputError,
        InputGroup,
        InputGroupText,
        BetterTooltip,
        Pill,
        PickingRedFlagAlert,
        Multiselect,
    },

    data() {
        return {
            mode: 'picking',
            notesCountdown: 5,
            notesCountdownReady: false,
            notesCountdownInterval: null,

            saveOrderPackagingTimeout: {},
            saveOrderToteCountTimeout: {},

            showQuantityModal: false,
            quantity: null,
            selectedItem: null,

            picker: this.origPicker,
            showPickerCodeModal: false,
            pickerCode: null,
            pickerCodeError: null,
            pickerCodeErrorTimeout: null,
            
            isWholesale: this.orders[0].is_wholesale == 1,
            pickOneByOne: !(this.orders[0].is_wholesale == 1),

            updatingOrderItem: false,
            errorMessage: null,

            itemsGroupedByZone: this.order_items,

            selectedZone: null,

            // Overlay for layer placement
            showLayerOverlay: false,
            layerOverlayMessage: '',
            layerOverlayTimeout: null,
            overlayProgress: 100,
            layerOverlayKey: 0,
        };
    },

    methods: {
        pickingRedFlagActive(order) {
            return (order.is_wholesale && order.customer?.picking_red_flag)
                || (!order.is_wholesale && order.retail_customer?.picking_red_flag);
        },

        notesModeCustomerName(order) {
            if (order.is_wholesale) {
                return order.customer?.name ?? '';
            }
            const rc = order.retail_customer;
            if (rc?.shipping_first_name || rc?.shipping_last_name) {
                return [rc.shipping_first_name, rc.shipping_last_name].filter(Boolean).join(' ');
            }
            return [order.shipping_first_name, order.shipping_last_name].filter(Boolean).join(' ');
        },

        backUrl () {
            this.$inertia.visit(route('picking.index'));
        },

        enterNotesMode() {
            this.notesCountdown = 5;
            this.notesCountdownReady = false;
            if (this.notesCountdownInterval) {
                clearInterval(this.notesCountdownInterval);
                this.notesCountdownInterval = null;
            }
            this.notesCountdownInterval = setInterval(() => {
                this.notesCountdown -= 1;
                if (this.notesCountdown <= 0) {
                    clearInterval(this.notesCountdownInterval);
                    this.notesCountdownInterval = null;
                    this.notesCountdownReady = true;
                }
            }, 1000);
            this.mode = 'notes';
        },

        acknowledgeNotes() {
            if (!this.notesCountdownReady) return;
            this.mode = 'picking';
            this.initPickingZones();
        },

        initPickingZones() {
            if (this.isWholesale) {
                const hasPickedItems = this.flatOrderItems.some(item => item.picking_status !== null);

                if (hasPickedItems) {
                    if (this.hasPalletOrders) {
                        this.selectedZone = 'items';
                    } else {
                        let nextZone = this.getNextZone();
                        if (nextZone === null) {
                            const firstZone = Object.keys(this.itemsGroupedByZone)[0];
                            if (firstZone) {
                                this.selectedZone = firstZone;
                            }
                        } else {
                            this.selectedZone = nextZone;
                        }
                    }
                } else {
                    this.selectedZone = 'initial_label';
                }
            } else {
                let nextZone = this.getNextZone();
                if (nextZone === null) {
                    const firstZone = Object.keys(this.itemsGroupedByZone)[0];
                    if (firstZone) {
                        this.selectedZone = firstZone;
                    }
                } else {
                    this.selectedZone = nextZone;
                }
            }
        },

        getLayerIconClass(orderIndex) {
            const totalOrders = this.orderIdsForIndicator.length;
            
            // Determine layer position
            if (orderIndex === 0) {
                return 'fa-solid fa-arrow-up'; // Top layer
            } else if (orderIndex === totalOrders - 1) {
                return 'fa-solid fa-arrow-down'; // Bottom layer
            } else {
                return 'fa-solid fa-equals'; // Middle layer
            }
        },

        getLayerPositionHtml(orderIndex) {
            const totalOrders = this.orders.length;
            
            if (totalOrders === 1) {
                return '<i class="fa-solid fa-layer-group"></i>&nbsp;&nbsp;Place Item on Any Layer&nbsp;&nbsp;<i class="fa-solid fa-layer-group"></i>';
            }
            
            if (orderIndex === 0) {
                return '<i class="fa-solid fa-arrow-up"></i>&nbsp;&nbsp;Place Item on Top Layer&nbsp;&nbsp;<i class="fa-solid fa-arrow-up"></i>';
            } else if (orderIndex === totalOrders - 1) {
                return '<i class="fa-solid fa-arrow-down"></i>&nbsp;&nbsp;Place Item on Bottom Layer&nbsp;&nbsp;<i class="fa-solid fa-arrow-down"></i>';
            } else {
                return '<i class="fa-solid fa-equals"></i>&nbsp;&nbsp;Place Item on Middle Layer&nbsp;&nbsp;<i class="fa-solid fa-equals"></i>';
            }
        },

        showLayerPlacementOverlay(orderIndex) {
            this.layerOverlayMessage = this.getLayerPositionHtml(orderIndex);
            this.layerOverlayKey++; // Force re-animation
            this.showLayerOverlay = true;
            this.overlayProgress = 100;
            
            // Clear any existing timeout
            if (this.layerOverlayTimeout) {
                clearTimeout(this.layerOverlayTimeout);
            }
            
            // Animate progress bar
            const startTime = Date.now();
            const duration = 5000; // 5 seconds
            
            const animateProgress = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.max(0, 100 - (elapsed / duration) * 100);
                
                this.overlayProgress = progress;
                
                if (progress > 0) {
                    requestAnimationFrame(animateProgress);
                }
            };
            
            requestAnimationFrame(animateProgress);
            
            // Hide overlay after 5 seconds
            this.layerOverlayTimeout = setTimeout(() => {
                this.showLayerOverlay = false;
                this.layerOverlayMessage = '';
                this.overlayProgress = 100;
            }, 5000);
        },

        autoSaveOrderPackaging (order_id) {
            this.saveOrderDebounce('Packaging', order_id, () => {
                let order = this.orders.find(order => order.id == order_id);
                
                if (order.box_count || order.pallet_count) order.packaged_at = DateTime.now();
                else order.packaged_at = null;
                order.updated_at = DateTime.now();

                axios.post(route('orders.update_packaging_counts', {order: order.id, box_count: order.box_count, pallet_count: order.pallet_count}));
            });
        },

        autoSaveOrderToteCount (order_id) {
            this.saveOrderDebounce('ToteCount', order_id, () => {
                let order = this.orders.find(order => order.id == order_id);
                
                order.updated_at = DateTime.now();

                axios.post(route('orders.update_tote_count', {order: order.id, tote_count: order.tote_count}));
            });
        },
        
        // Tell the callback to happen shortly unless interrupted by an update
        saveOrderDebounce: function (type, order_id, callback = null)
        {
            if (this['saveOrder' + type + 'Timeout'][order_id] != undefined) clearTimeout(this['saveOrder' + type + 'Timeout'][order_id]);
            
            if (typeof callback == 'function')
            {
                this['saveOrder' + type + 'Timeout'][order_id] = setTimeout(() => {
                    callback();
                    clearTimeout(this['saveOrder' + type + 'Timeout'][order_id]);
                }, 250);
            }
        },

        updateSelectedZone () {
            console.log('updateSelectedZone', this.selectedZone);
            // For wholesale orders, handle initial_label zone
            if (this.isWholesale && this.selectedZone === 'initial_label') {
                if (this.hasPalletOrders) this.selectedZone = 'items';
                else {
                    let nextZone = this.getNextZone();
                    if (nextZone === null) {
                        const firstZone = Object.keys(this.itemsGroupedByZone)[0];
                        if (firstZone) {
                            this.selectedZone = firstZone;
                        }
                    } else {
                        this.selectedZone = nextZone;
                    }
                }
                return;
            }

            // For wholesale orders with pallet orders, stay on 'items' until all items are picked
            if (this.isWholesale && this.hasPalletOrders) {
                if (this.allItemPickedAndHasQuantity) {
                    this.selectedZone = 'summary';
                }
                return;
            }

            if (this.selectedZone === 'items') {
                if (this.allItemPickedAndHasQuantity) {
                    this.selectedZone = 'summary';
                }
                return;
            }

            if (this.isZoneFullyPicked(this.selectedZone)) this.selectedZone = this.getNextZone();
        },

        updatePickerIfNeeded (response = null, callback = null) {
            if (response === null || response === 'invalid_picker_session')
            {
                this.picker = null;
                this.showPickerCode();
            }
            else if (typeof callback == 'function') callback();
        },

        markOrderPicked(order_id){
            let order = this.orders.find(order => order.id == order_id);
            order.picked_at = DateTime.now();

            axios.post(`/orders/${order_id}/markPicked`)
            .then((response) => {

                if (this.allOrdersPicked && this.allItemPickedAndHasQuantity) this.backUrl();
            });
        },

        updatePickedQuantityByOne(itemId) {
            let item = this.flatOrderItems.find(item => item.id === itemId) || null;
            if (!item) return;

            this.selectedItem = item;
            if (this.selectedItem.picked_quantity < this.selectedItem.quantity) {
                this.selectedItem.picked_quantity += 1;
            }
        },

        updateOrderItem(item, status) {
            this.updatingOrderItem = true;
            this.selectedItem = item;

            axios.post(`/order_item/${this.selectedItem.id}/pick`, { status })
            .then((response) => {
                this.updatePickerIfNeeded(response.data, () => {
                    Object.assign(this.selectedItem, response.data);

                    // Find the order index for this item
                    const orderIndex = this.orders.findIndex(order => 
                        this.itemsGroupedByZone[order.id][this.selectedZone]?.some(zoneItem => zoneItem.id === item.id)
                    );
                    
                    if (!this.isWholesale && orderIndex !== -1) {
                        this.showLayerPlacementOverlay(orderIndex);
                    }

                    this.updateSelectedZone();
                })
            })
            .catch((error) => {
                this.errorMessage = error.response?.data?.message || error.message;
            })
            .finally(() => {
                this.updatingOrderItem = false;
            });
        },

        shortOrderItem() {
            axios.post(route('orders.items.short.pick', { order_item: this.selectedItem.id, quantity: this.quantity }))
            .then((response) => {
                this.updatePickerIfNeeded(response.data, () => {
                    this.closeQuantityModal();
                    let item = this.flatOrderItems.find(item => item.id === response.data.id) || null;
                    if (item) {
                        Object.assign(item, response.data);

                        // Find the order index for this item
                        const orderIndex = this.orders.findIndex(order => 
                            this.itemsGroupedByZone[order.id][this.selectedZone]?.some(zoneItem => zoneItem.id === item.id)
                        );
                        
                        if (!this.isWholesale && orderIndex !== -1) {
                            this.showLayerPlacementOverlay(orderIndex);
                        }

                        this.updateSelectedZone();
                    }
                })
            })
            .catch((error) => {
                this.errorMessage = error.response?.data?.message || error.message;
            });
        },

        setSelectedItem(id){
            this.selectedItem = this.flatOrderItems.find(item => item.id === id) || null;
        },

        resetSkippedItems () {
            this.flatOrderItems.forEach(item => {
                item.skipped = false;
            });
        },

        updatePicker() {
            this.pickerCodeError = null;
            if (this.pickerCodeErrorTimeout) clearTimeout(this.pickerCodeErrorTimeout);

            axios.post(route('orders.update_picker', { picker_code: this.pickerCode }))
            .then((response) => {
                if (response.data == 'invalid_picker_code')
                {
                    this.pickerCodeError = 'Invalid Picker Code';
                    this.pickerCodeErrorTimeout = setTimeout(() => {
                        this.pickerCodeError = null;
                    }, 4 * 1000);
                }
                else
                {
                    this.picker = response.data;
                    this.cancelPickerCode();
                }
            });
        },

        showPickerCode() {
            this.showPickerCodeModal = true;
        },

        cancelPickerCode() {
            this.showPickerCodeModal = false;
            this.pickerCode = null;
        },

        closeQuantityModal() {
            this.showQuantityModal = false;
            this.selectedItem = null;
            this.quantity = null;
        },

        handleShort(item){
            this.setSelectedItem(item.id);

            if(!this.pickOneByOne){
                this.showQuantityModal = true
            }else{
                this.quantity = item.picked_quantity;
                this.shortOrderItem();
            }
        },

        // Printing
        printLabels(order) {
            if (this.shouldAutoprint('labels')) {
                this.autoprintWithPrinterPrompt(route('orders.print'), { orders: order.id, printType: 'labels' })
                    .finally(() => {
                        setTimeout(()=> {
                            this.$inertia.reload({ only: ['load'] });
                        }, 250);
                    });
            } else {
                window.open((route('orders.print', { orders: order.id, printType: 'labels'} )), "_blank");
                setTimeout(()=> {
                    this.$inertia.reload({ only: ['load'] });
                }, 250);
            }
        },

        printInitialLabels(order) {
            if (this.shouldAutoprint('initial_labels')) {
                axios.get(route('orders.print.initial_labels', { orders: order.id }))
                    .then(() => {
                        // Navigate to next zone after printing
                        this.updateSelectedZone();
                    });
            } else {
                window.open((route('orders.print.initial_labels', { orders: order.id })), "_blank");
                // Navigate to next zone after printing
                setTimeout(() => {
                    this.updateSelectedZone();
                }, 500);
            }
        },

        isZoneFullyPicked(zoneNumber) {
            // For wholesale orders with pallet orders, check if all items are picked
            if (this.isWholesale && this.hasPalletOrders) {
                return this.flatOrderItems.every(item => item.picking_status !== null);
            }
            
            let isZoneFullyPicked = true;

            this.orders.forEach(order => {
                if (this.itemsGroupedByZone[order.id][zoneNumber]?.some(item => item.picking_status === null)) isZoneFullyPicked = false;
            });

            return isZoneFullyPicked;
        },

        getNextZone () {
            let nextZone = undefined;

            if (this.allItemPickedAndHasQuantity) return 'summary';
            
            // For wholesale orders with pallet orders, return 'items' if there are unpicked items
            if (this.isWholesale && this.hasPalletOrders) {
                if (this.flatOrderItems.some(item => item.picking_status === null)) {
                    return 'items';
                }
                return 'summary';
            }
            
            if (this.selectedZone && this.selectedZone != 'summary' && this.selectedZone != 'initial_label' && this.selectedZone != this.lastZone)
                nextZone = this.flatOrderItems.find(item => item.picking_status === null && item.picking_zone > this.selectedZone);
            else nextZone = this.flatOrderItems.find(item => item.picking_status === null);

            console.log('nextZone', nextZone);
            
            if (nextZone !== undefined) return nextZone.picking_zone ?? '0';
            else return null;
        },

        itemUnitOfMeasure(item) {
            return item.size || item.product?.size || item.product?.unit_of_measure?.name || null;
        },

        getItemsForOrder(orderId) {
            // For wholesale orders with pallet orders, show all items when "Items" is selected
            if (this.isWholesale && this.hasPalletOrders && this.selectedZone === 'items') {
                return this.flatOrderItems.filter(item => item.order_id === orderId);
            }
            
            // For regular orders or when a specific zone is selected, show items for that zone
            if (this.itemsGroupedByZone[orderId] && this.itemsGroupedByZone[orderId][this.selectedZone]) {
                return this.itemsGroupedByZone[orderId][this.selectedZone];
            }
            
            return [];
        },

    },

    computed: {
        pickedQuantityStep () {
            return this.selectedItem ? orderItemQuantityStep(this.selectedItem) : 1;
        },

        orderIds () {
            return Object.keys(this.orders);
        },

        orderIdsForIndicator () {
            if (this.orderIds.length == 1) return [...this.orderIds, ...this.orderIds]
            return this.orderIds
        },

        zones () {
            let zones = [];
            this.orders.forEach(order => {
                zones = [...zones, ...Object.keys(this.order_items[order.id])];
            });

            zones = [...new Set(zones)].sort((a, b) => {
                return a - b;
            });
            
            return zones;
        },

        flatOrderItems () {
            let flatOrderItems = [];
            this.orders.forEach(order => {
                flatOrderItems = [...flatOrderItems, ...Object.values(this.order_items[order.id])];
            });
            
           return flatOrderItems.flat().sort((a, b) => (a.picking_zone ?? 0) - (b.picking_zone ?? 0));
        },

        lastZone () {
            return this.zones[this.zones.length - 1];
        },

        pickingStarted () {
            return this.flatOrderItems.some(item => item.picking_status !== null);
        },

        countItemsUnprocessed() {
            return this.flatOrderItems.filter(item => item.picking_status === null).length;
        },

        allItemsPicked() {
            return this.flatOrderItems.every((item) => {
                return item.picking_status !== null;
            });
        },

        allItemPickedAndHasQuantity() {
            return this.flatOrderItems.every((item) => {
                return item.picking_status !== null && item.picked_quantity !== null;
            });
        },

        allOrdersPicked() {
            return this.orders.every(order => order.picked_at !== null);
        },

        hasPalletOrders() {
            return this.orders.some(order => order.customer && order.customer.pallet_orders);
        },

        ordersRequiringNotesAcknowledgement() {
            return this.orders.filter((order) => {
                if (order.picked_at !== null) {
                    return false;
                }
                if (order.is_wholesale) {
                    return (order.customer?.picking_notes && order.customer.picking_notes.trim() !== '')
                        || (order.picking_notes && order.picking_notes.trim() !== '')
                        || order.customer?.picking_red_flag;
                }
                return Boolean(order.retail_customer_id && order.retail_customer?.picking_red_flag);
            });
        },
    },

    watch: {
        mode () {
            this.resetSkippedItems();
        },

        showPickerCodeModal () {
            setTimeout(() => {
                if (this.$refs.pickerCode) {
                    this.$refs.pickerCode.focus();
                }
            }, 100);
        }
    },

    created() {
        if (this.requires_notes_acknowledgement) {
            this.enterNotesMode();
        } else {
            this.initPickingZones();
        }
    },

    beforeUnmount() {
        if (this.notesCountdownInterval) {
            clearInterval(this.notesCountdownInterval);
            this.notesCountdownInterval = null;
        }
    },
};
</script>

<style scoped>
    .item_picked {
        @apply bg-green bg-opacity-20 !important;
    }
    .item_shorted {
        @apply bg-yellow-400 bg-opacity-20 !important;
    }
    
    .animate-fade-in {
        animation: fadeIn 0.3s ease-out;
    }

    @keyframes fadeIn {
        0% {
            opacity: 0.75;
        }
        100% {
            opacity: 1;
        }
    }
</style>